import { Component, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, map, of, switchMap } from 'rxjs';
import { ContractorsService } from 'src/app/services/contractors.service';
import { TestDataKind, testDataKinds, TestDataService } from 'src/app/services/test-data.service';

@Component({
  selector: 'app-test-data',
  imports: [TranslatePipe],
  templateUrl: './test-data.component.html',
  styleUrl: './test-data.component.scss',
})
export class TestDataComponent {
  readonly service = inject(TestDataService);
  private readonly contractors = inject(ContractorsService);
  readonly open = signal(false);
  readonly organization = signal('');
  readonly scenario = signal('');
  readonly kinds = testDataKinds.filter(kind => kind !== 'schedules');
  readonly randomKinds: readonly TestDataKind[] = ['users', 'sites', 'tasks'];
  readonly counts = signal<Record<TestDataKind, number>>({ clients: 0, users: 0, sites: 0, projects: 0, periods: 0, tasks: 0, schedules: 0 });
  readonly statuses = signal<Partial<Record<TestDataKind, boolean>>>({});
  readonly total = computed(() => Object.values(this.counts()).reduce((sum, count) => sum + count, 0));
  readonly valid = computed(() => this.organization() && this.total() > 0 && this.total() <= 100
    && Object.values(this.counts()).every(count => Number.isInteger(count) && count >= 0 && count <= 30));

  readonly directory = toSignal(toObservable(this.service.available).pipe(
    switchMap(available => available ? this.contractors.getList().pipe(
      map(items => ({ items, error: false })), catchError(() => of({ items: [], error: true })),
    ) : of({ items: [], error: false })),
  ), { initialValue: { items: [], error: false } });

  readonly created = computed(() => Object.entries(this.service.progress()?.created ?? {}).filter(([collection]) => collection !== 'workforce_v2.work_segments').map(([collection, ids]) => ({
    label: ({ 'workforce_v2.work_days': 'schedules', 'site-areas': 'areas', 'site-projects': 'projects', 'site-project-periods': 'periods' } as Record<string, string>)[collection] ?? collection,
    count: ids.length,
  })));

  setCount(kind: TestDataKind, value: string) {
    this.counts.update(counts => ({ ...counts, [kind]: Number(value) }));
  }

  setRandom(kind: TestDataKind, checked: boolean) {
    this.statuses.update(statuses => ({ ...statuses, [kind]: checked }));
  }

  generate() {
    if (this.valid()) {
      this.service.generate(this.organization(), this.scenario(), this.counts(), this.statuses());
    }
  }
}
